import React, { useCallback, useRef, useState } from "react";
import classNames from "classnames";
import { isAxiosError } from "axios";
import {
    INVOICE_TEMPLATE_LIST_MAX_LIMIT,
    InvoiceTemplateDetail,
    invoiceTemplateNameSchema,
} from "@common/schemas/domains/invoice/invoice-template.schema";
import { buildDuplicateInvoiceTemplateName } from "@common/helpers/invoice-template.helper";
import type { NativeInvoicePaymentMethod } from "@common/constants/nativeInvoicePaymentMethod";
import { InvoiceTemplateArchivedFilter } from "@common/types/invoiceTemplate";
import {
    useDefaultLayout,
    useGroupRef,
    type Layout,
    type LayoutChangedMeta,
} from "react-resizable-panels";
import { Address } from "@common/types/embeddable/address";
import { Entity } from "@common/types/entity";
import { getAddress } from "@api/entity.api";
import { useInvoiceTemplatesForEntities } from "@api/invoiceTemplate.api";
import { getAddressQueryKey, queryClient } from "queryClient";
import { InvoiceStatus } from "@common/types/invoice";
import {
    NativeInvoiceCapability,
    canUseNativeInvoiceCapability,
} from "@common/helpers/native-invoice-editability";
import { NativeInvoiceAdjustmentSettings } from "@common/schemas/domains/invoice/native-invoice-adjustment-settings.schema";
import {
    NativeInvoiceCustomTermValue,
    NativeInvoiceTermType,
} from "@common/helpers/invoice-terms.helper";
import {
    NativeInvoiceEmailSnapshot,
    NativeInvoiceRenderSnapshot,
} from "@common/types/nativeInvoice";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useConstant } from "@hooks/useConstant";
import { Drawer } from "@design-system/Drawer/Drawer";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@design-system/ResizablePanels/ResizablePanels";
import type {
    NativeInvoiceCreatePrefill,
    NativeInvoiceCustomer,
    NativeInvoiceLineCodingByLineId,
} from "./nativeInvoiceFormTypes";
import { createNativeInvoiceFormStateFromTemplate } from "./createNativeInvoiceFormStateFromTemplate";
import type { CreateNativeInvoiceFormStateResult } from "./createNativeInvoiceFormState";
import { useInvoiceTemplateContentSave } from "./useInvoiceTemplateContentSave";
import { useInvoiceTemplateCreateSave } from "./useInvoiceTemplateCreateSave";
import { getNativeInvoiceDisplayStatus } from "./getNativeInvoiceDisplayStatus";
import { ConfirmApplyTemplateModal } from "./ConfirmApplyTemplateModal";
import { InvoiceTemplateCreatedToast } from "./InvoiceTemplateCreatedToast";
import { ConfirmDiscardChangesModal } from "./ConfirmDiscardChangesModal";
import { NATIVE_INVOICE_NEGATIVE_TOTAL_TOOLTIP } from "@components/invoices/invoiceLockedFieldTooltip";
import { DetailsPanel } from "./DetailsPanel";
import { type InvoiceFieldSettingsDraft } from "./invoiceFieldSettings";
import { type InvoiceFieldSettingsOpenSection } from "./InvoiceFieldSettingsPanel";
import {
    takeNativeInvoiceItemSettingsSnapshot,
    type NativeInvoiceItemSettingsSnapshot,
} from "./nativeInvoicePerLineAdjustment";
import { EmailPreview } from "./EmailPreview";
import { resolveNativeInvoiceEmailPayUrl } from "./resolveNativeInvoiceEmailPayUrl";
import { NativeInvoiceGmailConnectProvider } from "./NativeInvoiceGmailConnect";
import {
    useNativeInvoiceSenderAddress,
    useResetStaleSendingEmail,
} from "./useNativeInvoiceSender";
import { InvoiceDocumentPreview } from "./InvoiceDocumentPreview";
import { NativeInvoiceCommandBar } from "./NativeInvoiceCommandBar";
import {
    NativeInvoiceDraftHeader,
    NativeInvoiceDrawerMode,
} from "./NativeInvoiceDraftHeader";
import { NativeInvoiceFormProvider } from "./NativeInvoiceFormProvider";
import { SendEmailPanel } from "./SendEmailPanel";
import { useApplyInvoiceTemplate } from "./useApplyInvoiceTemplate";
import { useNativeInvoiceHandlers } from "./useNativeInvoiceHandlers";
import { createNativeInvoiceFormState } from "./createNativeInvoiceFormState";
import {
    applyAddressToFromState,
    fromStateFromEntity,
} from "./copyNativeInvoicePartyDetails";
import { getNativeInvoiceFromEntityLock } from "./fromEntityLock";
import { hasNonZeroNativeInvoiceLine } from "./computeNativeInvoiceTotals";
import { isNativeInvoiceFormStateEmpty } from "./isNativeInvoiceFormEmpty";
import { firstNonEmptyString } from "./nativeInvoiceFormHelpers";
import { useNativeInvoiceForm } from "./useNativeInvoiceForm";
import { useNativeInvoiceCloseGuard } from "./useNativeInvoiceCloseGuard";
import { useNativeInvoiceSave, type SaveResult } from "./useNativeInvoiceSave";
import { useInvoiceFlight } from "./useInvoiceFlight";
import { playSidePanelStepExit } from "./sidePanelStepExit";
import { useNativeInvoicePdfDownload } from "./useNativeInvoicePdfDownload";
import {
    NativeInvoicePdfOnlyCommandBar,
    NativeInvoicePdfOnlyPreview,
} from "./NativeInvoicePdfOnlyView";
import { ResendInvoiceEmailModal } from "./ResendInvoiceEmailModal";
import { SendTestInvoiceEmailModal } from "./SendTestInvoiceEmailModal";
import { useOverlays } from "contexts/OverlayContext";
import { NativeInvoiceStatusBar } from "./NativeInvoiceStatusBar";
import { FieldSettingsCommandBar } from "./FieldSettingsCommandBar";
import { TemplateCreationCommandBar } from "./TemplateCreationCommandBar";
import { INVOICE_SCROLL_CONTAINER_ID } from "./invoiceSectionAnchors";
import { usePreviewViewportHeight } from "./usePreviewViewportHeight";
import styles from "./NativeInvoiceDrawer.module.scss";
import panelStyles from "./SidePanel.module.scss";

import { type NativeInvoiceWorkspaceStep } from "./nativeInvoiceWorkspaceSteps";

const PREVIEW_PANEL_ID = "native-invoice-preview-panel";
const SIDE_PANEL_ID = "native-invoice-side-panel";

// react-resizable-panels sets `height: 100%` and `overflow: hidden` inline on
// the group, and `overflow: auto` inline on every panel's inner wrapper. CSS
// can't override inline styles, so the single-scroller layout has to pass
// these as style props. Every value here is load-bearing: a clipping
// ancestor would become the floating preview's sticky scrollport instead of
// the workspace body, and without `align-self` the body's flex line would
// stretch the group to the viewport height — leaving the sticky preview no
// room to travel, so it would scroll away with the form.
//
// `minHeight` keeps a short form (templates hide dates / invoice number, so
// the left column is often shorter than the window) from collapsing the
// group — and the fitted preview — to the form's height. Live invoices are
// tall enough that this only matters when those fields are gone.
const FLOW_GROUP_STYLE: React.CSSProperties = {
    alignSelf: "flex-start",
    height: "auto",
    minHeight: "var(--invoice-preview-height, 100%)",
    minWidth: 0,
    overflow: "visible",
};
// Only the block axis can stay visible. Dropping the library's `overflow:
// auto` also dropped its horizontal clipping, so content that can't shrink to
// the column (a wide grid, the preview strips at `min-width: max-content`)
// escaped the panel and put a horizontal scrollbar on the body. `clip` is not
// a scroll container, so clipping the inline axis keeps the panel out of the
// scrollport chain and the floating preview still sticks to the body.
const FLOW_PANEL_STYLE: React.CSSProperties = {
    minWidth: 0,
    overflowX: "clip",
    overflowY: "visible",
};

// Magnetic snap while dragging the divider: within this distance (in
// percentage points) of an even split, the layout locks to exactly 50/50 so
// the user can feel when they've hit it.
const EVEN_SPLIT = 50;
const EVEN_SPLIT_SNAP_TOLERANCE = 1.5;

// A group measured while its container has no width (drawer still animating
// open, panels mid-mount) reports zero-sized panels. Restoring or persisting
// such a layout collapses the preview pane on every subsequent open, so these
// layouts are ignored in both directions.
function isDegenerateLayout(layout: Layout): boolean {
    return Object.values(layout).some(
        (size) => !Number.isFinite(size) || size <= 0,
    );
}

function resolveTemplateWorkflowSaver({
    isCreateTemplateMode,
    isTemplateMode,
    createTemplateSaver,
    templateSaver,
    invoiceSaver,
}: {
    isCreateTemplateMode: boolean;
    isTemplateMode: boolean;
    createTemplateSaver: SaveResult;
    templateSaver: SaveResult;
    invoiceSaver: SaveResult;
}): SaveResult {
    if (isCreateTemplateMode) {
        return createTemplateSaver;
    }
    if (isTemplateMode) {
        return templateSaver;
    }
    return invoiceSaver;
}

interface NativeInvoiceFormWorkspaceProps {
    open: boolean;
    onClose: () => void;
    workspaceId: string | undefined;
    initialInvoiceId?: string;
    // Create sessions pass DRAFT. Only FORM_CONTENT (Draft) opens the editor;
    // every other status is a PDF preview.
    invoiceStatus: InvoiceStatus;
    entities: Entity[];
    selectedEntity: Entity;
    onSelectEntityId: (entityId: number | null) => void;
    suggestedInvoiceNumber: string;
    currentUserName?: string | null;
    currentUserEmail?: string | null;
    initialRenderSnapshot?: NativeInvoiceRenderSnapshot | null;
    initialCustomer?: NativeInvoiceCustomer | null;
    initialLogoUrl?: string | null;
    initialSendingEmailId?: string | null;
    initialCollectPayment?: boolean;
    initialPaymentToken?: string | null;
    initialStripeFinancialAccountId?: number | null;
    initialAcceptedPaymentMethods?: NativeInvoicePaymentMethod[];
    initialEmailSnapshot?: NativeInvoiceEmailSnapshot | null;
    initialTermType?: NativeInvoiceTermType | null;
    initialCustomTerm?: NativeInvoiceCustomTermValue | null;
    initialIsEmailSubjectAutomatic?: boolean;
    initialIsEmailMessageAutomatic?: boolean;
    initialLineCodingByLineId?: NativeInvoiceLineCodingByLineId;
    initialAdjustmentSettings?: NativeInvoiceAdjustmentSettings | null;
    primaryAccrualLedgerId?: string;
    incomeAccountLedgerId?: string;
    // Create-mode only: pre-fills the draft instead of the blank single-line
    // default. See NativeInvoiceCreatePrefill.
    createPrefill?: NativeInvoiceCreatePrefill;
    entityAddress?: Address | null;
    drawerMode?: NativeInvoiceDrawerMode;
    templateDetail?: InvoiceTemplateDetail;
    sourceTemplateId?: string;
    templateId?: string;
    templateName?: string;
    // Template sessions only: announces a successful save (templates list).
    onTemplateSaved?: () => void;
    // This session is stacked on top of another invoice sheet, so its own
    // sheet arrives over a scrim that separates the two layers.
    isStacked?: boolean;
    onAnimationEnd?: (open: boolean) => void;
}

export const NativeInvoiceFormWorkspace: React.FC<
    NativeInvoiceFormWorkspaceProps
> = ({
    open,
    onClose,
    workspaceId,
    initialInvoiceId,
    invoiceStatus,
    entities,
    selectedEntity,
    onSelectEntityId,
    suggestedInvoiceNumber,
    currentUserName,
    currentUserEmail,
    initialRenderSnapshot,
    initialCustomer,
    initialLogoUrl,
    initialSendingEmailId,
    initialCollectPayment,
    initialPaymentToken,
    initialStripeFinancialAccountId,
    initialAcceptedPaymentMethods,
    initialEmailSnapshot,
    initialTermType,
    initialCustomTerm,
    initialIsEmailSubjectAutomatic,
    initialIsEmailMessageAutomatic,
    initialLineCodingByLineId,
    initialAdjustmentSettings,
    primaryAccrualLedgerId,
    incomeAccountLedgerId,
    createPrefill,
    entityAddress,
    drawerMode = "create",
    templateDetail,
    sourceTemplateId,
    templateId,
    templateName,
    onTemplateSaved,
    isStacked,
    onAnimationEnd,
}) => {
    // v4: discards layouts saved while a sizing bug let panes drift to the
    // 40/60 clamp, so everyone restarts at the even split (same reset pattern
    // as the v2/v3 bumps).
    const { defaultLayout, onLayoutChanged } = useDefaultLayout({
        groupId: "native-invoice-workspace-panel-group-v4",
        storage: localStorage,
    });
    const panelGroupRef = useGroupRef();
    const bodyRef = usePreviewViewportHeight();

    const restoredLayout =
        defaultLayout && !isDegenerateLayout(defaultLayout)
            ? defaultLayout
            : undefined;

    const handlePanelLayoutSnap = useCallback(
        (layout: Layout) => {
            const sidePanelSize = layout[SIDE_PANEL_ID];
            if (
                sidePanelSize !== undefined &&
                sidePanelSize !== EVEN_SPLIT &&
                Math.abs(sidePanelSize - EVEN_SPLIT) <=
                    EVEN_SPLIT_SNAP_TOLERANCE
            ) {
                panelGroupRef.current?.setLayout({
                    [SIDE_PANEL_ID]: EVEN_SPLIT,
                    [PREVIEW_PANEL_ID]: EVEN_SPLIT,
                });
            }
        },
        [panelGroupRef],
    );

    const handlePanelLayoutChanged = useCallback(
        (layout: Layout, meta: LayoutChangedMeta) => {
            if (isDegenerateLayout(layout)) {
                return;
            }
            onLayoutChanged(layout, meta);
        },
        [onLayoutChanged],
    );

    const isCreateTemplateMode = drawerMode === "createTemplate";
    const isTemplateMode = drawerMode === "template" || isCreateTemplateMode;
    // Duplicating an existing template (templateDetail hydrated from the
    // source while in create-template mode) seeds the name field with a
    // "Copy of X" default, matching the ledger duplicate pattern
    // (LedgerFormModal) and the catalog item / close template duplicate
    // actions elsewhere in the app. The default is clamped to the schema's
    // max length so long source names don't open the drawer with an invalid
    // name. A plain "New template" session has no source template, so it
    // starts blank as before.
    const [draftTemplateName, setDraftTemplateName] = useState(() =>
        isCreateTemplateMode && templateDetail
            ? buildDuplicateInvoiceTemplateName(templateDetail.name)
            : "",
    );
    const [templateNameError, setTemplateNameError] = useState<string | null>(
        null,
    );
    const isTemplateNameValid =
        invoiceTemplateNameSchema.safeParse(draftTemplateName).success;
    const templateNameInputRef = useRef<HTMLInputElement>(null);
    const canEditFormContent =
        isTemplateMode ||
        canUseNativeInvoiceCapability({
            status: invoiceStatus,
            capability: NativeInvoiceCapability.FORM_CONTENT,
        });
    const isPdfOnly = !isTemplateMode && !canEditFormContent;

    const initial = useConstant(() => {
        if (templateDetail) {
            return createNativeInvoiceFormStateFromTemplate({
                detail: templateDetail,
                entity: selectedEntity,
                primaryAccrualLedgerId,
                incomeAccountLedgerId,
            });
        }
        if (initialInvoiceId && initialRenderSnapshot) {
            return createNativeInvoiceFormState({
                mode: "edit",
                snapshot: initialRenderSnapshot,
                entity: selectedEntity,
                customer: initialCustomer,
                logoPreviewUrl: initialLogoUrl,
                lineCodingByLineId: initialLineCodingByLineId,
                currentUserName,
                currentUserEmail,
                sendingEmailId: initialSendingEmailId,
                collectPayment: initialCollectPayment,
                stripeFinancialAccountId: initialStripeFinancialAccountId,
                acceptedPaymentMethods: initialAcceptedPaymentMethods,
                emailSnapshot: initialEmailSnapshot,
                termType: initialTermType,
                customTerm: initialCustomTerm,
                isEmailSubjectAutomatic: initialIsEmailSubjectAutomatic,
                isEmailMessageAutomatic: initialIsEmailMessageAutomatic,
                adjustmentSettings: initialAdjustmentSettings,
            });
        }
        return createNativeInvoiceFormState({
            mode: "create",
            entity: selectedEntity,
            entityAddress,
            currentUserName,
            currentUserEmail,
            prefill: createPrefill,
        });
    });

    const form = useNativeInvoiceForm({
        initial,
        suggestedInvoiceNumber,
        defaultEntity: selectedEntity,
        currentUserName,
        currentUserEmail,
        primaryAccrualLedgerId,
        incomeAccountLedgerId,
        isTemplateMode,
    });
    const {
        state,
        setState,
        hasInvoiceNumberOverride,
        lockInvoiceNumber,
        draftBody,
        draftLineCodingBaseline,
        dirtyFingerprint,
        subtotal,
        total,
        customerEmail,
        replyToEmail,
        replyToName,
        toEmails,
        ccEmails,
        bccEmails,
        fromName,
        onSelectCustomer,
        acknowledgeSavedLineCoding,
        applyTemplateFormState,
    } = form;
    // The From entity is immutable once an invoice exists (it drives
    // accounting/JE posting and branding), when billing selected expenses so
    // the draft stays on the transactions' entity, and when any line is a
    // catalog item — those items are entity-scoped, so switching From would
    // mix invoice entity with catalog entity.
    const { isLocked: isFromEntityLocked, tooltip: fromEntityLockedTooltip } =
        getNativeInvoiceFromEntityLock({
            isExistingInvoice: Boolean(initialInvoiceId),
            hasBillableExpenses: Boolean(
                createPrefill?.billableExpenseTransactionIds?.length,
            ),
            isTemplateBound: Boolean(templateDetail),
            lines: state.lines,
        });
    const senderAddress = useNativeInvoiceSenderAddress(state.sendingEmailId);

    const setSendingEmailId = useCallback(
        (sendingEmailId: string | null) =>
            setState((prev) => ({ ...prev, sendingEmailId })),
        [setState],
    );

    useResetStaleSendingEmail({
        sendingEmailId: state.sendingEmailId,
        entityUuid: selectedEntity?.uuid,
        onChange: setSendingEmailId,
    });

    const [savedBranding, setSavedBranding] = useState<{
        logoUrl: string | null;
        monogram: string | null;
    } | null>(null);
    const [paymentToken, setPaymentToken] = useState<string | null>(
        initialPaymentToken ?? null,
    );

    // Assigned once the saver exists (it is created further down, from state
    // this callback feeds): applying a template must not read as unsaved work.
    const rebaseBaselineAfterTemplateApplyRef = useRef<() => void>(
        () => undefined,
    );

    const handleApplyTemplateFormState = useCallback(
        (next: CreateNativeInvoiceFormStateResult) => {
            applyTemplateFormState(next);
            setSavedBranding(null);
            rebaseBaselineAfterTemplateApplyRef.current();
        },
        [applyTemplateFormState],
    );

    const isDirtyRef = useRef(false);
    // Gates the template-change override confirmation: a freshly-opened,
    // untouched "Create Invoice" form applies a template straight away, but
    // any other non-empty draft (including one populated by a previously
    // applied template the user hasn't edited since — non-empty but not
    // dirty) must confirm first. See isNativeInvoiceFormStateEmpty.
    const isFormEmptyRef = useRef(isNativeInvoiceFormStateEmpty(state));

    // Latest From entity a switch requested. Async address fetches below check
    // it by id so a slower response for a previously selected entity can't
    // overwrite a newer selection — including two entities that share a business
    // name, where a name-only guard would accept the stale address.
    const latestFromEntityIdRef = useRef<number | null>(selectedEntity.id);

    // Applying a cross-entity template switches the From entity along with the
    // form state. The template state carries its own From block, so this only
    // moves the selection (and invalidates any in-flight address fetch from a
    // previous manual switch) — none of handleSelectFromEntity's from-reset or
    // address fetching must run.
    const handleTemplateEntitySwitch = useCallback(
        (templateEntityId: number) => {
            latestFromEntityIdRef.current = templateEntityId;
            onSelectEntityId(templateEntityId);
        },
        [onSelectEntityId],
    );

    const isTemplateRequired = !initialInvoiceId && !isTemplateMode;
    const { templates: activeTemplates, isLoading: areTemplatesLoading } =
        useInvoiceTemplatesForEntities({
            entityIds: entities.map((entity) => entity.id),
            archivedFilter: InvoiceTemplateArchivedFilter.ACTIVE,
            page: 1,
            limit: INVOICE_TEMPLATE_LIST_MAX_LIMIT,
        });

    const templateApply = useApplyInvoiceTemplate({
        workspaceId,
        entityId: selectedEntity.id,
        selectedEntity,
        entities,
        primaryAccrualLedgerId,
        incomeAccountLedgerId,
        initialTemplateId: sourceTemplateId,
        applyFormState: handleApplyTemplateFormState,
        isFromEntityLocked,
        onTemplateEntitySwitch: handleTemplateEntitySwitch,
        isFormEmptyRef,
        isDirtyRef,
    });

    const invoiceSaver = useNativeInvoiceSave({
        workspaceId,
        entityId: selectedEntity.id,
        initialInvoiceId,
        draft: draftBody,
        draftLineCodingBaseline,
        dirtyFingerprint,
        billableExpenseTransactionIds:
            createPrefill?.billableExpenseTransactionIds,
        sourceTemplateId: templateApply.appliedTemplateId,
        onSaveSuccess: (result, { savedLineCodingBaseline }) => {
            acknowledgeSavedLineCoding(savedLineCodingBaseline);
            if (result.nativeDetail.renderSnapshot) {
                setSavedBranding({
                    logoUrl: result.nativeDetail.renderSnapshot.logoUrl ?? null,
                    monogram:
                        result.nativeDetail.renderSnapshot.monogram ?? null,
                });
                // Pin the invoice number to what was just persisted, so a
                // later save on this same draft can't pick up a higher
                // suggestion from the invalidated last-invoice-number query
                // and silently renumber the invoice (see lockInvoiceNumber).
                const persistedInvoiceNumber =
                    result.nativeDetail.renderSnapshot.invoiceNumber;
                if (persistedInvoiceNumber) {
                    lockInvoiceNumber(persistedInvoiceNumber);
                }
            }
            setPaymentToken(result.nativeDetail.paymentToken);
        },
    });
    const templateSaver = useInvoiceTemplateContentSave({
        workspaceId,
        entityId: selectedEntity.id,
        templateId,
        draft: draftBody,
        draftLineCodingBaseline,
        dirtyFingerprint,
        onSaveSuccess: ({ savedLineCodingBaseline }) => {
            acknowledgeSavedLineCoding(savedLineCodingBaseline);
        },
    });
    const createTemplateSaver = useInvoiceTemplateCreateSave({
        workspaceId,
        entityId: selectedEntity.id,
        name: draftTemplateName,
        draft: draftBody,
        draftLineCodingBaseline,
        dirtyFingerprint,
        onSaveSuccess: ({ savedLineCodingBaseline }) => {
            acknowledgeSavedLineCoding(savedLineCodingBaseline);
        },
    });
    const saver = resolveTemplateWorkflowSaver({
        isCreateTemplateMode,
        isTemplateMode,
        createTemplateSaver,
        templateSaver,
        invoiceSaver,
    });

    // Fresh dirty flag for async callbacks: the switch handler's closure is
    // captured at click time, but the address `.then` must fold into the
    // baseline only when the form is *still* clean when the address lands.
    isDirtyRef.current = saver.isDirty;
    isFormEmptyRef.current = isNativeInvoiceFormStateEmpty(state);
    rebaseBaselineAfterTemplateApplyRef.current =
        saver.rebaseBaselineAfterTemplateApply;

    const { acknowledgeEntityRescope, rebaseCleanBaselineAfterAddress } = saver;
    const { applyTemplateImmediately, clearTemplateSelection } = templateApply;
    const handleSelectFromEntity = useCallback(
        (entity: Entity | undefined) => {
            // Defense in depth: the picker is disabled when locked, but guard
            // the handler too so a programmatic call can't slip through.
            if (isFromEntityLocked) {
                return;
            }
            if (isTemplateRequired) {
                const entityTemplate = entity
                    ? activeTemplates.find(
                          (template) => template.entityId === entity.id,
                      )
                    : undefined;
                // When the target entity has a template, apply it so the
                // entity switch and template stay atomic. With no template
                // (none selected, or this entity has none), switch From
                // directly — do not no-op and leave only the sender email
                // updated by the picker.
                if (entityTemplate) {
                    void applyTemplateImmediately(
                        entityTemplate.id,
                        entityTemplate.entityId,
                    );
                    return;
                }
            }
            latestFromEntityIdRef.current = entity?.id ?? null;
            // A manual entity change disconnects the draft from any applied
            // template: the template belongs to the previous entity, so
            // keeping the link would mix one entity's template with another
            // entity's invoice.
            clearTemplateSelection();
            // Announced before the updates below land: once the From block has
            // been rewritten from the new entity, the rescope is
            // indistinguishable from a user edit and the close guard would
            // report the switch alone as unsaved work.
            acknowledgeEntityRescope(entity?.id);
            onSelectEntityId(entity?.id ?? null);
            setState((prev) => {
                const from = fromStateFromEntity({
                    entity,
                    fallbackEmail: currentUserEmail,
                });
                // No explicit override (the user never typed a number —
                // lockInvoiceNumber's pin is a separate mechanism that does
                // not set this) means the field is still "untouched": clear
                // it so the live suggestion for the newly selected entity
                // can apply. A user-typed number is left alone. Checking the
                // override (not comparing `prev.invoiceNumber` to the live
                // suggestion prop) keeps this correct once a save has
                // pinned the resolved value away from that live prop. The
                // pin itself is dropped by useNativeInvoiceForm on this same
                // entity change (see its defaultEntity-id reset).
                const isInvoiceNumberUntouched = !hasInvoiceNumberOverride;
                return {
                    ...prev,
                    from,
                    logoPath: null,
                    logoPreviewUrl: null,
                    ...(isInvoiceNumberUntouched ? { invoiceNumber: "" } : {}),
                    collectPayment: false,
                    stripeFinancialAccountId: null,
                };
            });
            setSavedBranding(null);
            if (!entity) {
                return;
            }
            void queryClient
                .fetchQuery({
                    queryKey: getAddressQueryKey(entity.id),
                    queryFn: () => getAddress(entity.id),
                    staleTime: 10 * 60 * 1000,
                })
                .then((address) => {
                    // A newer switch superseded this fetch — drop the stale
                    // response rather than writing it onto the current entity.
                    if (latestFromEntityIdRef.current !== entity.id) {
                        return;
                    }
                    // Capture cleanliness before the write so we only fold an
                    // untouched draft's baseline; a real edit made during the
                    // fetch must stay dirty.
                    const wasClean = !isDirtyRef.current;
                    setState((prev) => ({
                        ...prev,
                        from: applyAddressToFromState({
                            from: prev.from,
                            address,
                        }),
                    }));
                    if (wasClean) {
                        rebaseCleanBaselineAfterAddress(entity.id);
                    }
                })
                .catch(() => undefined);
        },
        [
            isFromEntityLocked,
            isTemplateRequired,
            activeTemplates,
            acknowledgeEntityRescope,
            rebaseCleanBaselineAfterAddress,
            onSelectEntityId,
            setState,
            currentUserEmail,
            hasInvoiceNumberOverride,
            applyTemplateImmediately,
            clearTemplateSelection,
        ],
    );

    const handlers = useNativeInvoiceHandlers({
        workspaceId,
        entityId: selectedEntity.id,
        saver,
        draftBody,
        toEmails,
        state,
        total,
        onClose,
        isTemplateMode,
    });
    const isSaveDisabled =
        handlers.saveDisabled ||
        handlers.hasNegativeTotal ||
        (isCreateTemplateMode && !isTemplateNameValid);
    const hasNonZeroLineItem = hasNonZeroNativeInvoiceLine(state.lines);

    const [step, setStep] = useState<NativeInvoiceWorkspaceStep>("details");
    const [showResendModal, setShowResendModal] = useState(false);
    const [resendFormKey, setResendFormKey] = useState(0);
    const [testEmailInvoiceId, setTestEmailInvoiceId] = useState<string | null>(
        null,
    );

    // Snapshot taken when the Fields & visibility view opens (non-null =
    // view open). Save drops it; Cancel restores both groups of edits.
    const [settingsViewSnapshot, setSettingsViewSnapshot] = useState<{
        fieldSettings: InvoiceFieldSettingsDraft;
        itemSettings: NativeInvoiceItemSettingsSnapshot;
        openSection: InvoiceFieldSettingsOpenSection;
    } | null>(null);
    const isFieldSettingsViewOpen = settingsViewSnapshot !== null;

    const { error: toastError, success: toastSuccess } = useToaster();

    const persistDraft = useCallback(async (): Promise<string | undefined> => {
        // The backend rejects a negative total on every create/update, so block
        // all draft-persisting paths here — including the close-guard's "Save
        // changes & exit", which reaches this without a disabled button of its
        // own — rather than letting the save fail as a generic "Failed to save"
        // toast.
        if (!canEditFormContent || handlers.hasNegativeTotal) {
            return undefined;
        }
        try {
            return await saver.save();
        } catch (error) {
            if (
                isCreateTemplateMode &&
                isAxiosError(error) &&
                typeof error.response?.data?.message === "string"
            ) {
                setTemplateNameError(error.response.data.message);
                return undefined;
            }
            // The backend client's response interceptor already toasts the
            // server's error message; the generic fallback only covers
            // messageless failures so exactly one toast shows either way.
            if (!isAxiosError(error) || !error.response?.data?.message) {
                toastError("Failed to save");
            }
            return undefined;
        }
    }, [
        canEditFormContent,
        handlers.hasNegativeTotal,
        saver,
        isCreateTemplateMode,
        toastError,
    ]);

    const handleSave = useCallback(async (): Promise<boolean> => {
        const savedId = await persistDraft();
        if (!savedId) {
            return false;
        }
        if (isCreateTemplateMode) {
            toastSuccess(
                <InvoiceTemplateCreatedToast
                    templateId={savedId}
                    entityId={selectedEntity.id}
                />,
            );
            onClose();
        } else if (isTemplateMode) {
            onTemplateSaved?.();
            onClose();
        } else {
            toastSuccess("Invoice saved");
        }
        return true;
    }, [
        persistDraft,
        isCreateTemplateMode,
        isTemplateMode,
        onTemplateSaved,
        toastSuccess,
        onClose,
        selectedEntity.id,
    ]);

    // "Create only" issues the invoice as OPEN and ends the session: the
    // invoice exists and the user skipped the send step, so there is nothing
    // left in the drawer to come back to.
    const handleCreateOnly = useCallback(async () => {
        if (
            !canEditFormContent ||
            !hasNonZeroLineItem ||
            handlers.isInvoiceDateLocked ||
            handlers.hasNegativeTotal
        ) {
            return;
        }
        try {
            const savedId = await saver.save({ status: InvoiceStatus.OPEN });
            if (!savedId) {
                return;
            }
            toastSuccess("Invoice created");
            onClose();
        } catch (error) {
            if (!isAxiosError(error) || !error.response?.data?.message) {
                toastError("Failed to save");
            }
        }
    }, [
        canEditFormContent,
        hasNonZeroLineItem,
        handlers.isInvoiceDateLocked,
        handlers.hasNegativeTotal,
        saver,
        toastSuccess,
        toastError,
        onClose,
    ]);

    // "Create template and invoice" (create-template mode): persists the new
    // template, then swaps this drawer for a fresh create-invoice session with
    // that template applied — same flow as the created-toast's Create invoice
    // button, except the overlay is replaced in place (the key change remounts
    // the drawer) instead of the user clicking through the toast.
    const { openOverlay } = useOverlays();
    const handleCreateTemplateAndInvoice = useCallback(async () => {
        if (isSaveDisabled) {
            return;
        }
        const savedId = await persistDraft();
        if (!savedId) {
            return;
        }
        toastSuccess("Template created");
        openOverlay("nativeInvoiceDrawer", {
            applyTemplate: {
                templateId: savedId,
                entityId: selectedEntity.id,
            },
        });
    }, [
        isSaveDisabled,
        persistDraft,
        toastSuccess,
        openOverlay,
        selectedEntity.id,
    ]);

    // "Save as template" persists the draft, then swaps this drawer for the
    // single-step create-template flow hydrated from it (name field focused,
    // email settings editable there) — same session the templates list's
    // "From invoice" opens.
    const handleSaveAsTemplate = useCallback(async () => {
        const savedId = await persistDraft();
        if (!savedId) {
            return;
        }
        openOverlay("nativeInvoiceDrawer", {
            createTemplate: true,
            invoiceId: savedId,
        });
    }, [persistDraft, openOverlay]);

    const handleOpenTestEmail = useCallback(async () => {
        if (isSaveDisabled) {
            return;
        }
        try {
            const invoiceId = await saver.save();
            if (invoiceId) {
                setTestEmailInvoiceId(invoiceId);
            }
        } catch (error) {
            if (!isAxiosError(error) || !error.response?.data?.message) {
                toastError("Couldn't prepare test email");
            }
        }
    }, [isSaveDisabled, saver, toastError]);

    const closeGuard = useNativeInvoiceCloseGuard({
        isEditable: canEditFormContent,
        isDirty: saver.isDirty,
        onClose,
        onSave: handleSave,
    });

    const pdfDownload = useNativeInvoicePdfDownload({
        workspaceId,
        entityId: selectedEntity.id,
        invoiceNumber: state.invoiceNumber,
    });

    const handleDownload = useCallback(async () => {
        if (isPdfOnly) {
            if (!initialInvoiceId) {
                return;
            }
            await pdfDownload.downloadInvoicePdf(initialInvoiceId);
            return;
        }
        await handlers.handleDownload();
    }, [isPdfOnly, initialInvoiceId, pdfDownload, handlers]);

    const canResendInvoice =
        invoiceStatus === InvoiceStatus.OPEN &&
        Boolean(initialEmailSnapshot?.to) &&
        Boolean(initialInvoiceId) &&
        Boolean(workspaceId);

    const displayStatus = getNativeInvoiceDisplayStatus({
        status: invoiceStatus,
        due: state.dueDate || null,
        nativeDetail: {
            scheduledSendAt: null,
            sentAt: null,
        },
    });

    const effectiveStep: NativeInvoiceWorkspaceStep =
        canEditFormContent && !isTemplateMode && step === "send"
            ? "send"
            : "details";

    const { captureInvoiceForFlight, captureEmailForFlight } = useInvoiceFlight(
        effectiveStep === "send" ? "send" : "details",
    );
    const handleSetStep = useCallback(
        (next: NativeInvoiceWorkspaceStep) => {
            if (next === effectiveStep) {
                return;
            }
            if (next === "send" && (isSaveDisabled || !hasNonZeroLineItem)) {
                return;
            }
            // The departing panel's content slides away in the direction of
            // travel while the entering panel's sections slide in behind it.
            playSidePanelStepExit(next === "details" ? "backward" : "forward");
            // Both steps share one scroll surface, so the entering panel would
            // otherwise open at the departing panel's offset. The exit ghost is
            // already detached from the scrollport, so it stays put.
            bodyRef.current?.scrollTo({ top: 0 });
            if (effectiveStep === "details" && next === "send") {
                captureInvoiceForFlight();
            } else if (effectiveStep === "send" && next === "details") {
                captureEmailForFlight();
            }
            setStep(next);
        },
        [
            captureInvoiceForFlight,
            captureEmailForFlight,
            effectiveStep,
            hasNonZeroLineItem,
            isSaveDisabled,
            bodyRef,
        ],
    );

    const showDetailsPanel = canEditFormContent && effectiveStep === "details";
    const showSendPanel = canEditFormContent && effectiveStep === "send";

    // Slide only after the user has left Details. The drawer always starts
    // there, so visiting Send is enough to latch; going back is backward.
    const hasNavigatedPanelRef = useRef(false);
    if (effectiveStep === "send") {
        hasNavigatedPanelRef.current = true;
    }
    const isStepBackward =
        effectiveStep === "details" && hasNavigatedPanelRef.current;
    const sidePanelKey = effectiveStep;

    const invoicePreviewProps = {
        state: isTemplateMode ? { ...state, poNumber: "" } : state,
        entity: selectedEntity,
        customerEmail,
        subtotal,
        total,
        savedBranding,
        senderEmail: senderAddress,
    };
    // Thumbnail variant (email attachment card): width-scaled only, since its
    // container's height follows the paper.
    const invoicePreview = (
        <InvoiceDocumentPreview {...invoicePreviewProps} paperChrome="flush" />
    );
    // Pane variant: also fits the pane's height so the page is never cropped.
    const invoicePreviewFitted = (
        <InvoiceDocumentPreview {...invoicePreviewProps} fitToViewportHeight />
    );

    const emailPreviewProps = {
        fromName,
        fromEmail: senderAddress,
        replyToEmail,
        replyToName,
        customerName: firstNonEmptyString(
            state.customer?.name,
            "Add a customer",
        ),
        toEmails,
        ccEmails,
        bccEmails,
        emailSubject: state.emailSubject,
        emailMessage: state.emailMessage,
        payUrl: resolveNativeInvoiceEmailPayUrl({
            isCollectPaymentOn: state.collectPayment,
            paymentToken,
            origin: window.location.origin,
        }),
        attachPdf: state.emailAttachPdf,
        invoiceNumber: state.invoiceNumber,
        invoiceThumbnail: invoicePreview,
        onDownloadInvoice: handleDownload,
        isDownloading: handlers.isDownloading,
    };

    return (
        <NativeInvoiceFormProvider state={state} setState={setState}>
            <NativeInvoiceGmailConnectProvider
                onSenderConnected={setSendingEmailId}
            >
                <Drawer
                    open={open}
                    onClose={closeGuard.requestClose}
                    onAnimationEnd={onAnimationEnd}
                    size="fullScreen"
                    noPadding
                    hasBackdrop={isStacked}
                    initialFocus={
                        isCreateTemplateMode ? templateNameInputRef : undefined
                    }
                    headerClassName={styles.flatHeader}
                    headerAddonLeft={
                        <NativeInvoiceDraftHeader
                            mode={drawerMode}
                            invoiceNumber={state.invoiceNumber}
                            templateName={
                                isCreateTemplateMode
                                    ? draftTemplateName
                                    : templateName
                            }
                            displayStatus={displayStatus}
                        />
                    }
                >
                    <div className={styles.workspace}>
                        {!isPdfOnly && canEditFormContent && (
                            <NativeInvoiceStatusBar
                                currentStep={effectiveStep}
                                context={
                                    isTemplateMode ? "template" : "invoice"
                                }
                            />
                        )}
                        <div
                            id={INVOICE_SCROLL_CONTAINER_ID}
                            ref={bodyRef}
                            className={styles.body}
                        >
                            {isPdfOnly ? (
                                <NativeInvoicePdfOnlyPreview
                                    invoicePreview={invoicePreviewFitted}
                                />
                            ) : (
                                <>
                                    <ResizablePanelGroup
                                        style={FLOW_GROUP_STYLE}
                                        orientation="horizontal"
                                        groupRef={panelGroupRef}
                                        defaultLayout={restoredLayout}
                                        onLayoutChange={handlePanelLayoutSnap}
                                        onLayoutChanged={
                                            handlePanelLayoutChanged
                                        }
                                    >
                                        <ResizablePanel
                                            defaultSize="50%"
                                            minSize="40%"
                                            maxSize="60%"
                                            id={SIDE_PANEL_ID}
                                            style={FLOW_PANEL_STYLE}
                                        >
                                            <aside
                                                key={sidePanelKey}
                                                className={classNames(
                                                    styles.sidePanel,
                                                    hasNavigatedPanelRef.current &&
                                                        panelStyles.stepAnimated,
                                                    isStepBackward &&
                                                        panelStyles.stepBackward,
                                                )}
                                                // The step-exit snapshot clones
                                                // this element (sidePanelStepExit).
                                                data-invoice-side-panel=""
                                            >
                                                {showDetailsPanel && (
                                                    <DetailsPanel
                                                        subtotal={subtotal}
                                                        total={total}
                                                        entities={entities}
                                                        selectedEntity={
                                                            selectedEntity
                                                        }
                                                        invoiceId={
                                                            initialInvoiceId
                                                        }
                                                        onSelectEntity={
                                                            handleSelectFromEntity
                                                        }
                                                        onSelectCustomer={
                                                            onSelectCustomer
                                                        }
                                                        onChangeLine={
                                                            form.updateLine
                                                        }
                                                        onAddLine={form.addLine}
                                                        onRemoveLine={
                                                            form.removeLine
                                                        }
                                                        isFromEntityLocked={
                                                            isFromEntityLocked
                                                        }
                                                        fromEntityLockedTooltip={
                                                            fromEntityLockedTooltip
                                                        }
                                                        hideInvoiceNumber={
                                                            isTemplateMode
                                                        }
                                                        isTemplateMode={
                                                            isTemplateMode
                                                        }
                                                        fieldSettings={
                                                            state.fieldSettings
                                                        }
                                                        onFieldSettingsChange={(
                                                            next,
                                                        ) =>
                                                            setState(
                                                                (current) => ({
                                                                    ...current,
                                                                    fieldSettings:
                                                                        next,
                                                                }),
                                                            )
                                                        }
                                                        isFieldSettingsViewOpen={
                                                            isFieldSettingsViewOpen
                                                        }
                                                        fieldSettingsOpenSection={
                                                            settingsViewSnapshot?.openSection
                                                        }
                                                        onOpenFieldSettings={(
                                                            openSection,
                                                        ) => {
                                                            setSettingsViewSnapshot(
                                                                {
                                                                    fieldSettings:
                                                                        structuredClone(
                                                                            state.fieldSettings,
                                                                        ),
                                                                    itemSettings:
                                                                        takeNativeInvoiceItemSettingsSnapshot(
                                                                            state,
                                                                        ),
                                                                    openSection,
                                                                },
                                                            );
                                                        }}
                                                        templateName={
                                                            isCreateTemplateMode
                                                                ? draftTemplateName
                                                                : undefined
                                                        }
                                                        templateNameError={
                                                            templateNameError ??
                                                            undefined
                                                        }
                                                        onTemplateNameChange={
                                                            isCreateTemplateMode
                                                                ? (value) => {
                                                                      setDraftTemplateName(
                                                                          value,
                                                                      );
                                                                      setTemplateNameError(
                                                                          null,
                                                                      );
                                                                  }
                                                                : undefined
                                                        }
                                                        templateNameInputRef={
                                                            isCreateTemplateMode
                                                                ? templateNameInputRef
                                                                : undefined
                                                        }
                                                        templateSelect={
                                                            isTemplateMode
                                                                ? undefined
                                                                : {
                                                                      entities,
                                                                      templates:
                                                                          activeTemplates,
                                                                      isLoading:
                                                                          areTemplatesLoading,
                                                                      selectedTemplateId:
                                                                          templateApply.selectedTemplateId,
                                                                      isApplying:
                                                                          templateApply.isApplying,
                                                                      onSelectTemplate:
                                                                          templateApply.requestApplyTemplate,
                                                                      onClose,
                                                                  }
                                                        }
                                                    />
                                                )}
                                                {showSendPanel && (
                                                    <SendEmailPanel />
                                                )}
                                            </aside>
                                        </ResizablePanel>
                                        <ResizableHandle
                                            className={
                                                styles.invisibleResizeHandle
                                            }
                                        />
                                        <ResizablePanel
                                            defaultSize="50%"
                                            minSize="40%"
                                            maxSize="60%"
                                            id={PREVIEW_PANEL_ID}
                                            style={FLOW_PANEL_STYLE}
                                        >
                                            <div
                                                className={classNames(
                                                    styles.previewPane,
                                                    styles.previewPaneFloating,
                                                )}
                                            >
                                                {effectiveStep === "send" ? (
                                                    <EmailPreview
                                                        {...emailPreviewProps}
                                                    />
                                                ) : (
                                                    <InvoiceDocumentPreview
                                                        {...invoicePreviewProps}
                                                        fitToViewportHeight
                                                    />
                                                )}
                                            </div>
                                        </ResizablePanel>
                                    </ResizablePanelGroup>
                                </>
                            )}
                        </div>
                        {isPdfOnly && (
                            <NativeInvoicePdfOnlyCommandBar
                                onClose={closeGuard.requestClose}
                                onDownload={() => {
                                    void handleDownload();
                                }}
                                isDownloading={pdfDownload.isDownloading}
                                onResend={
                                    canResendInvoice
                                        ? () => {
                                              setResendFormKey(
                                                  (key) => key + 1,
                                              );
                                              setShowResendModal(true);
                                          }
                                        : undefined
                                }
                            />
                        )}
                        {isCreateTemplateMode &&
                            !isPdfOnly &&
                            !isFieldSettingsViewOpen && (
                                <TemplateCreationCommandBar
                                    open={open}
                                    saveStatus={saver.status}
                                    isDisabled={isSaveDisabled}
                                    hasNegativeTotal={handlers.hasNegativeTotal}
                                    onCreate={() => {
                                        void handleSave();
                                    }}
                                    onCreateAndInvoice={() => {
                                        void handleCreateTemplateAndInvoice();
                                    }}
                                />
                            )}
                        {!isPdfOnly && isFieldSettingsViewOpen && (
                            <FieldSettingsCommandBar
                                onCancel={() => {
                                    if (settingsViewSnapshot) {
                                        setState((current) => ({
                                            ...current,
                                            fieldSettings:
                                                settingsViewSnapshot.fieldSettings,
                                            ...settingsViewSnapshot.itemSettings,
                                        }));
                                    }
                                    setSettingsViewSnapshot(null);
                                }}
                                onSave={() => {
                                    setSettingsViewSnapshot(null);
                                }}
                            />
                        )}
                        {!isCreateTemplateMode &&
                            !isPdfOnly &&
                            !isFieldSettingsViewOpen && (
                                <NativeInvoiceCommandBar
                                    step={effectiveStep}
                                    open={open}
                                    shouldAnimateStepChange={
                                        hasNavigatedPanelRef.current
                                    }
                                    saveStatus={saver.status}
                                    // A negative total is passed separately as
                                    // `hasNegativeTotal` and composed inside the
                                    // command bar (like the period lock) so the
                                    // menu caret stays reachable; folding it into
                                    // `saveDisabled` here would disable the whole
                                    // caret and hide the per-item tooltips.
                                    saveDisabled={handlers.saveDisabled}
                                    hasCounterparty={state.customer?.id != null}
                                    hasNonZeroLineItem={hasNonZeroLineItem}
                                    isInvoiceDateLocked={
                                        handlers.isInvoiceDateLocked
                                    }
                                    hasNegativeTotal={handlers.hasNegativeTotal}
                                    sendDisabled={handlers.sendDisabled}
                                    sendDisabledTooltip={
                                        handlers.sendDisabledTooltip
                                    }
                                    isSending={handlers.isSending}
                                    isDownloading={handlers.isDownloading}
                                    scheduleEnabled={state.scheduleEnabled}
                                    canSave={canEditFormContent}
                                    canSend={
                                        canEditFormContent && !isTemplateMode
                                    }
                                    canDownload={!isTemplateMode}
                                    saveButtonLabel={
                                        isTemplateMode
                                            ? "Save template"
                                            : "Create"
                                    }
                                    onSetStep={handleSetStep}
                                    onDownload={handleDownload}
                                    onSave={() => {
                                        void handleSave();
                                    }}
                                    onCreateOnly={() => {
                                        void handleCreateOnly();
                                    }}
                                    onSaveAsTemplate={() => {
                                        void handleSaveAsTemplate();
                                    }}
                                    onSend={handlers.handleSend}
                                    onSendTestEmail={() => {
                                        void handleOpenTestEmail();
                                    }}
                                />
                            )}
                    </div>
                    <ConfirmDiscardChangesModal
                        show={closeGuard.isDiscardModalOpen}
                        isSaving={saver.status === "saving"}
                        saveDisabled={handlers.hasNegativeTotal}
                        saveDisabledTooltip={
                            handlers.hasNegativeTotal
                                ? NATIVE_INVOICE_NEGATIVE_TOTAL_TOOLTIP
                                : undefined
                        }
                        onSave={closeGuard.saveAndClose}
                        onDiscard={onClose}
                        onCancel={closeGuard.cancelClose}
                    />
                    <ConfirmApplyTemplateModal
                        show={templateApply.isConfirmOpen}
                        onCancel={templateApply.cancelPendingTemplate}
                        onApply={templateApply.confirmPendingTemplate}
                    />
                    {workspaceId && testEmailInvoiceId && (
                        <SendTestInvoiceEmailModal
                            key={testEmailInvoiceId}
                            show
                            onClose={() => setTestEmailInvoiceId(null)}
                            workspaceId={workspaceId}
                            entityId={selectedEntity.id}
                            invoiceId={testEmailInvoiceId}
                            initialRecipient={currentUserEmail ?? ""}
                        />
                    )}
                    {workspaceId &&
                        initialInvoiceId &&
                        initialEmailSnapshot?.to && (
                            <ResendInvoiceEmailModal
                                key={resendFormKey}
                                show={showResendModal}
                                onClose={() => setShowResendModal(false)}
                                workspaceId={workspaceId}
                                entityId={selectedEntity.id}
                                invoiceId={initialInvoiceId}
                                from={senderAddress}
                                to={initialEmailSnapshot.to}
                                initialSubject={state.emailSubject}
                                initialMessage={state.emailMessage}
                            />
                        )}
                </Drawer>
            </NativeInvoiceGmailConnectProvider>
        </NativeInvoiceFormProvider>
    );
};
